import {useMemo, useRef, useState} from 'react';

import {PreSaveImmediatelyMiddleware} from 'procedures/ProcedurePageContext';
import removeNulls from 'util/removeNulls';

type Middleware = Record<string, {methodCid: string; fn: PreSaveImmediatelyMiddleware} | null>;

/**
 * Downstream components can register middleware, which is executed during `saveImmediately`.
 * This hook manages the list of middleware functions, but does not execute them.
 */
export default function usePreSaveImmediatelyMiddleware(): {
  addMiddleware: (methodId: string, fn: PreSaveImmediatelyMiddleware) => string;
  removeMiddleware: (id: string) => void;
  middleware: Array<{methodCid: string; fn: PreSaveImmediatelyMiddleware}>;
} {
  const [middleware, setMiddleware] = useState<Middleware>({});
  const nextId = useRef(1);

  const addMiddleware = (methodCid: string, fn: PreSaveImmediatelyMiddleware): string => {
    const id = `mw${nextId.current++}`;
    setMiddleware((m) => ({...m, [id]: {fn, methodCid}}));
    return id;
  };

  const removeMiddleware = (id: string): void => {
    setMiddleware((m) => ({...m, [id]: null}));
  };

  const middlewareWithoutNulls = useMemo(() => removeNulls(Object.values(middleware)), [middleware]);

  return {
    addMiddleware,
    removeMiddleware,
    middleware: middlewareWithoutNulls,
  };
}
